import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pill,
  Calendar,
  User,
  Bell,
  BellOff,
  Trash2,
  ChevronDown,
  ScanLine,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  History,
  Search,
  FileText,
} from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import prescriptionService, { Prescription } from '../services/prescription.service';

export default function PrescriptionHistoryPage() {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadPrescriptions();
  }, []);

  const loadPrescriptions = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await prescriptionService.getPrescriptions();
      setPrescriptions(result.prescriptions);
    } catch (err: any) {
      setError(err.message || 'Eroare la încărcarea rețetelor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await prescriptionService.deletePrescription(id);
      setPrescriptions(prev => prev.filter(p => p.id !== id));
      setConfirmDeleteId(null);
      setExpandedId(null);
    } catch (err: any) {
      alert(err.message || 'Eroare la ștergere');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReminderToggle = async (prescription: Prescription) => {
    const newValue = !prescription.reminderEnabled;
    setPrescriptions(prev =>
      prev.map(p => (p.id === prescription.id ? { ...p, reminderEnabled: newValue } : p))
    );
    try {
      await prescriptionService.updatePrescription(prescription.id, { reminderEnabled: newValue });
    } catch {
      setPrescriptions(prev =>
        prev.map(p => (p.id === prescription.id ? { ...p, reminderEnabled: !newValue } : p))
      );
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatPrescriptionDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const filtered = prescriptions.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.doctorName?.toLowerCase().includes(q) ||
      p.specialty?.toLowerCase().includes(q) ||
      p.medications.some(m => m.name.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-rose-100 rounded-xl flex items-center justify-center">
                  <History className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Istoric Rețete</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">
                    {prescriptions.length} {prescriptions.length === 1 ? 'rețetă salvată' : 'rețete salvate'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/prescription-scanner')}
              className="flex items-center gap-2 bg-rose-500 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-rose-600 transition-colors shadow-sm"
            >
              <ScanLine className="w-4 h-4" />
              <span className="hidden sm:inline">Scanează Rețetă</span>
              <span className="sm:hidden">Scanează</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-8">

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-rose-400 animate-spin" />
            <p className="text-sm text-gray-400">Se încarcă rețetele...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">Eroare</p>
              <p className="text-xs text-red-500">{error}</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && prescriptions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-5">
            <div className="w-24 h-24 bg-rose-50 rounded-3xl flex items-center justify-center">
              <div className="text-center">
                <p className="text-3xl font-bold text-rose-300">Rx</p>
                <div className="w-10 h-0.5 bg-rose-200 mx-auto mt-1" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-base font-semibold text-gray-700">Nicio rețetă scanată</p>
              <p className="text-sm text-gray-400 mt-1 max-w-xs">
                Scanează prima ta rețetă pentru a o salva în istoric și a gestiona medicamentele
              </p>
            </div>
            <button
              onClick={() => navigate('/prescription-scanner')}
              className="flex items-center gap-2 bg-rose-500 text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-sm hover:bg-rose-600 transition-colors"
            >
              <ScanLine className="w-4 h-4" />
              Scanează prima rețetă
            </button>
          </div>
        )}

        {/* Search + List */}
        {!loading && !error && prescriptions.length > 0 && (
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Caută după medic, specialitate sau medicament..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent"
              />
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{prescriptions.length}</p>
                <p className="text-xs text-gray-500 mt-0.5">Rețete totale</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-rose-600">
                  {prescriptions.reduce((sum, p) => sum + p.medications.length, 0)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Medicamente</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {prescriptions.filter(p => p.savedToProfile).length}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Salvate în profil</p>
              </div>
            </div>

            {/* No search results */}
            {filtered.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nicio rețetă găsită pentru "{searchQuery}"</p>
              </div>
            )}

            {/* Prescriptions grid - 2 columns on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map(prescription => (
                <div
                  key={prescription.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  {/* Card header */}
                  <button
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      setExpandedId(expandedId === prescription.id ? null : prescription.id)
                    }
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-11 h-11 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <p className="text-sm font-bold text-rose-400">Rx</p>
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {prescription.doctorName || 'Medic necunoscut'}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {prescription.specialty && (
                            <span className="text-xs text-gray-400">{prescription.specialty}</span>
                          )}
                          {prescription.specialty && <span className="text-gray-200 text-xs">•</span>}
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-300" />
                            <span className="text-xs text-gray-400">{formatDate(prescription.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="bg-rose-50 text-rose-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {prescription.medications.length} med.
                      </span>
                      {prescription.savedToProfile && (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      )}
                      {prescription.reminderEnabled && (
                        <Bell className="w-4 h-4 text-amber-500" />
                      )}
                      <ChevronDown
                        className={`w-4 h-4 text-gray-300 transition-transform ${
                          expandedId === prescription.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded content */}
                  {expandedId === prescription.id && (
                    <div className="border-t border-gray-50 px-5 pb-5 pt-4 space-y-4">
                      {/* Meta info */}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        {prescription.prescriptionDate && (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span>Data rețetei: {formatPrescriptionDate(prescription.prescriptionDate)}</span>
                          </div>
                        )}
                        {prescription.doctorName && (
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-gray-400" />
                            <span>{prescription.doctorName}</span>
                          </div>
                        )}
                      </div>

                      {/* Medications */}
                      <div className="space-y-2">
                        {prescription.medications.map((med, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5"
                          >
                            <div className="w-7 h-7 bg-rose-50 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Pill className="w-3.5 h-3.5 text-rose-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{med.name}</p>
                              <p className="text-xs text-gray-400">{med.quantity} • {med.frequency}</p>
                              {med.notes && <p className="text-xs text-gray-400 italic">{med.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Saved to profile indicator */}
                      {prescription.savedToProfile && (
                        <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2.5">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <p className="text-xs text-green-700 font-medium">
                            Medicamentele au fost salvate în profilul tău medical
                          </p>
                        </div>
                      )}

                      {/* Actions row */}
                      <div className="flex items-center justify-between pt-1">
                        <button
                          onClick={() => handleReminderToggle(prescription)}
                          className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl border transition-colors ${
                            prescription.reminderEnabled
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          {prescription.reminderEnabled ? (
                            <Bell className="w-3.5 h-3.5" />
                          ) : (
                            <BellOff className="w-3.5 h-3.5" />
                          )}
                          {prescription.reminderEnabled ? 'Reminder activ' : 'Activează reminder'}
                        </button>

                        <button
                          onClick={() => setConfirmDeleteId(prescription.id)}
                          className="flex items-center gap-1.5 text-xs font-medium text-red-400 px-3 py-2 rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Șterge
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Șterge rețeta</h3>
              <button onClick={() => setConfirmDeleteId(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              Ești sigur că vrei să ștergi această rețetă? Acțiunea nu poate fi anulată.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Anulează
              </button>
              <button
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={deletingId === confirmDeleteId}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-600 transition-colors"
              >
                {deletingId === confirmDeleteId ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {deletingId === confirmDeleteId ? 'Se șterge...' : 'Șterge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
